import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Settings,
  Lightbulb,
  TrendingUp,
  TrendingDown,
  Users,
  Download,
  CheckCircle2,
  Copy,
  ClipboardList,
  Play,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DifficultyIndicator } from '../../components/DifficultyIndicator'
import ExamDSideBySide, { type ExamDSideBySideDemoData } from '../../pages/ExamDSideBySide'
import { useDemoData } from '../useDemoData'
import type { ExamDDetail, SessionAnalysis, SessionGrades, ExamDifficulty } from '../../api/types'

// Every demo paper reuses the two sample scan pages shipped in /public/samples.
const DEMO_PAPER_PAGES = ['demo-paper-1', 'demo-paper-2']
const demoPaperUrl = (fileId: string) =>
  fileId === 'demo-paper-2' ? '/samples/student-paper-2.jpeg' : '/samples/student-paper.jpeg'

const mapDifficultyLevel = (
  difficulty?: ExamDifficulty,
): 'easy' | 'moderate' | 'hard' | 'very_hard' => {
  if (!difficulty) return 'moderate'
  switch (difficulty.category) {
    case 'Easy':
      return 'easy'
    case 'Hard':
      return 'hard'
    case 'Very Hard':
      return 'very_hard'
    case 'Moderate':
    default:
      return 'moderate'
  }
}

type MainTab = 'analysis' | 'students' | 'export'

const progressColor = (pct: number) => {
  if (pct >= 80) return 'success'
  if (pct >= 65) return 'copper'
  if (pct >= 50) return 'warning'
  return 'danger'
}

export default function DemoExamDetail() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation('pages')
  const examId = Number(sessionId)
  const { examDetails, examGrades, examAnalysis } = useDemoData()

  const detail = examDetails[examId] ?? null
  const grades: SessionGrades = examGrades[examId] ?? []
  const classAnalysis: SessionAnalysis | null = examAnalysis[examId] ?? null

  const [activeTab, setActiveTab] = useState<MainTab>('analysis')

  const hasGrades = grades.some((g) => g.paper_grade?.total_score != null)
  const questionsApproved = detail?.questions_approved ?? false

  const latestRun = detail?.latest_run || detail?.runs?.[0] || null
  const rubricFileId = detail?.rubric_file_id || null
  const hasRubric = Boolean(
    rubricFileId && rubricFileId !== 'null' && rubricFileId !== 'undefined',
  )

  // Data for the production-style side-by-side review (paper next to grades).
  const sideBySide = useMemo<ExamDSideBySideDemoData | null>(() => {
    if (!detail) return null
    const exam: ExamDDetail = {
      id: detail.id,
      exam_name: detail.session_name,
      subject: null,
      grade_level: null,
      test_date: detail.test_date,
      max_points: detail.max_points ?? 100,
      feedback_language: detail.feedback_language ?? 'English',
      class_id: detail.class_id ?? null,
      rubric_file_id: detail.rubric_file_id ?? null,
      questions_pdf_file_id: null,
      questions_json: detail.questions.map((q) => ({
        question_number: q.question_number,
        question_text: q.question_text ?? '',
        maximum_points: q.max_points ?? null,
        reference_answer: q.reference_answer,
      })),
      questions_approved: true,
      paper_count: detail.papers.length,
      latest_run_id: latestRun?.id ?? null,
      grading_mode: 'mod_d',
      submissions: detail.papers.map((paper) => ({
        id: paper.id,
        student_id: paper.student_id ?? 0,
        student_name: paper.student_name,
        student_no: '',
        file_ids: DEMO_PAPER_PAGES,
        original_filename: paper.original_filename,
        upload_timestamp: paper.upload_timestamp ?? null,
      })),
      runs: [],
      latest_run: null,
    }
    return { exam, grades, resolveFileUrl: demoPaperUrl }
  }, [detail, grades, latestRun])

  const classAverage = classAnalysis
    ? classAnalysis.class_average ||
      (() => {
        const averageScore = classAnalysis.question_stats.reduce(
          (sum, q) => sum + q.average_score,
          0,
        )
        const maxScore = classAnalysis.question_stats.reduce(
          (sum, q) => sum + q.max_points,
          0,
        )
        const successPercent = maxScore > 0 ? (averageScore / maxScore) * 100 : 0
        return {
          average_score: Math.round(averageScore * 100) / 100,
          max_score: maxScore,
          success_percent: Math.round(successPercent * 10) / 10,
        }
      })()
    : null

  // Not found
  if (!detail) {
    return (
      <div className="animate-fade-in">
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-danger-bg)',
            color: 'var(--color-danger)',
          }}
        >
          {t('demo.examDetail.emptyHeading')}
        </div>
      </div>
    )
  }

  // --- Analysis tab ---
  const renderAnalysis = () => {
    if (!hasGrades) {
      return (
        <div className="empty-state">
          <ClipboardList size={40} />
          <h3>{t('demo.examDetail.emptyHeading')}</h3>
          <p>
            {t('demo.examDetail.emptyBody')}
          </p>
          <button className="btn btn-copper btn-sm" disabled title={t('demo.examDetail.gradingDisabledTitle')}>
            <Play size={14} />
            {t('demo.examDetail.startGrading')}
          </button>
        </div>
      )
    }

    if (!classAnalysis) {
      return (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-danger-bg)',
            color: 'var(--color-danger)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {t('demo.examDetail.analysisError')}
        </div>
      )
    }

    return (
      <div className="animate-fade-in">
        {/* Analysis header with stats */}
        <div className="analysis-header">
          <h3 className="analysis-header-title">{t('demo.examDetail.analysisTitle')}</h3>
          <div className="analysis-header-stats">
            <span className="chip chip-grade">{t('demo.examDetail.nPapers', { count: classAnalysis.paper_count })}</span>
            {classAverage && (
              <span className="chip chip-grade">
                {t('demo.examDetail.classAvg')} {classAverage.average_score} / {classAverage.max_score} (
                {classAverage.success_percent}%)
              </span>
            )}
            <DifficultyIndicator difficulty={mapDifficultyLevel(classAnalysis.difficulty)} />
          </div>
        </div>

        {/* Strong / Weak topics */}
        <div className="analysis-grid">
          <div className="card card-padding">
            <div className="topics-panel-header">
              <div className="flex items-center gap-8">
                <TrendingUp size={16} style={{ color: 'var(--color-success)' }} />
                <span className="topics-panel-title">{t('demo.examDetail.strongTopics')}</span>
              </div>
              <span className="topics-panel-label">
                {t('demo.examDetail.topN', { n: Math.min(3, classAnalysis.analysis.successful_topics.length) })}
              </span>
            </div>
            {classAnalysis.analysis.successful_topics.length === 0 ? (
              <p className="text-muted text-sm">{t('demo.examDetail.noStrongTopics')}</p>
            ) : (
              classAnalysis.analysis.successful_topics.slice(0, 3).map((topic, i) => (
                <div key={i} className="topic-row">
                  <span className="topic-row-bullet" />
                  <span className="topic-row-name">{topic.topic}</span>
                  <span className="topic-row-pct text-success">{topic.avg_success_percent}%</span>
                </div>
              ))
            )}
          </div>

          <div className="card card-padding">
            <div className="topics-panel-header">
              <div className="flex items-center gap-8">
                <TrendingDown size={16} style={{ color: 'var(--color-danger)' }} />
                <span className="topics-panel-title">{t('demo.examDetail.weakTopics')}</span>
              </div>
              <span className="topics-panel-label">
                {t('demo.examDetail.bottomN', { n: Math.min(3, classAnalysis.analysis.weak_topics.length) })}
              </span>
            </div>
            {classAnalysis.analysis.weak_topics.length === 0 ? (
              <p className="text-muted text-sm">{t('demo.examDetail.noWeakTopics')}</p>
            ) : (
              classAnalysis.analysis.weak_topics.slice(0, 3).map((topic, i) => (
                <div key={i} className="topic-row">
                  <span className="topic-row-bullet" />
                  <span className="topic-row-name">{topic.topic}</span>
                  <span className="topic-row-pct text-danger">{topic.avg_success_percent}%</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recommendation */}
        <div className="recommendation-card">
          <div className="recommendation-icon">
            <Lightbulb />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="recommendation-label">{t('demo.examDetail.recommendation')}</div>
            <div className="recommendation-text">
              {classAnalysis.analysis.class_recommendation || t('demo.examDetail.noRecommendation')}
            </div>
          </div>
        </div>

        {/* Question performance */}
        <div className="card card-padding">
          <h3 className="card-title mb-16">{t('demo.examDetail.questionPerformance')}</h3>
          <div>
            {classAnalysis.question_stats.map((q) => {
              const success = Math.max(0, Math.min(100, q.success_percent))
              return (
                <div key={q.question_number} className="question-stat">
                  <div className="question-stat-number">
                    {t('demo.examDetail.questionPrefix')}
                    {q.question_number}
                  </div>
                  <div className="question-stat-text truncate">{q.question_text}</div>
                  <div className="question-stat-bar">
                    <div className="progress-bar">
                      <div
                        className={`progress-bar-fill ${progressColor(success)}`}
                        style={{ width: `${success}%` }}
                      />
                    </div>
                  </div>
                  <div className="question-stat-score">
                    {q.average_score}/{q.max_points}
                    <span className="text-xs text-muted" style={{ marginLeft: 6 }}>
                      ({Math.round(success)}%)
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  // --- Students tab ---
  // Mirrors production: the scanned paper is reviewed side by side with the
  // AI grades (Mod D layout), not in the legacy list/detail split view.
  const renderStudents = () => {
    if (!sideBySide) {
      return (
        <div className="empty-state">
          <Users size={40} />
          <h3>{t('demo.examDetail.noStudentResults')}</h3>
          <p>{t('demo.examDetail.noStudentResultsBody')}</p>
        </div>
      )
    }

    return (
      <div className="sbs-tab-container">
        <ExamDSideBySide demo={sideBySide} />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Exam detail top section */}
      <div className="exam-detail-top">
        <div className="exam-detail-info">
          <button
            className={hasRubric ? 'chip chip-check' : 'chip chip-papers'}
            onClick={() => navigate(`/demo/exams/${sessionId}/setup?tab=rubric`)}
          >
            {hasRubric ? <CheckCircle2 size={14} /> : <X size={14} />}
            {hasRubric ? t('demo.examDetail.rubricLoaded') : t('demo.examDetail.noRubric')}
          </button>
          <button
            className={
              detail.questions.length === 0
                ? 'chip chip-papers'
                : questionsApproved
                  ? 'chip chip-check'
                  : 'chip chip-papers'
            }
            onClick={() => navigate(`/demo/exams/${sessionId}/setup?tab=questions`)}
            style={
              detail.questions.length > 0 && !questionsApproved
                ? { background: '#f59e0b', color: '#fff', border: 'none' }
                : undefined
            }
          >
            {detail.questions.length > 0 ? <CheckCircle2 size={14} /> : <X size={14} />}
            {detail.questions.length > 0
              ? questionsApproved
                ? t('demo.examDetail.nQuestions', { count: detail.questions.length })
                : t('demo.examDetail.questionsAwaitingApproval', {
                    count: detail.questions.length,
                  })
              : t('demo.examDetail.questionsNotLoaded')}
          </button>
          <button
            className="chip chip-papers"
            onClick={() => navigate(`/demo/exams/${sessionId}/setup?tab=papers`)}
          >
            <Copy size={14} />
            {t('demo.examDetail.nPapers', { count: detail.papers.length })}
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => navigate(`/demo/exams/${sessionId}/setup`)}
          >
            <Settings size={14} />
            {t('demo.examDetail.editButton')}
          </button>
        </div>
        <div className="exam-detail-actions">
          <button
            className="btn btn-copper btn-sm"
            disabled
            title={t('demo.examDetail.gradingDisabledTitle')}
          >
            <Play size={14} />
            {t('demo.examDetail.startGrading')}
          </button>
        </div>
      </div>

      {/* Tabs — only show if there are grading results */}
      {classAnalysis && (
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'analysis' ? 'active' : ''}`}
            onClick={() => setActiveTab('analysis')}
          >
            {t('demo.examDetail.tabAnalysis')}
          </button>
          <button
            className={`tab ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}
          >
            {t('demo.examDetail.tabStudents')}
          </button>
          <button
            className={`tab ${activeTab === 'export' ? 'active' : ''}`}
            onClick={() => setActiveTab('export')}
          >
            {t('demo.examDetail.tabExport')}
          </button>
        </div>
      )}

      {/* Tab content */}
      {activeTab === 'analysis' && renderAnalysis()}
      {activeTab === 'students' && renderStudents()}
      {activeTab === 'export' && (
        <div className="animate-fade-in">
          <div className="coming-soon">
            <Download size={64} />
            <h3>{t('demo.examDetail.exportComingSoon')}</h3>
            <p className="text-muted">
              {t('demo.examDetail.exportComingSoonBody')}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
